import { FC, useCallback, useState } from "react";
import {
  Box,
  createStyles,
  Link,
  makeStyles,
  Typography,
} from "@material-ui/core";

import { ErrorMessage, Modal } from "components";
import { CONTACT_US_FORM_URL } from "constants/index";
import { formatMoney } from "lib/helpers";
import { DEFAULT_ERROR_MESSAGE } from "services/constants";
import { chargeInvestorWithStripe } from "services/payees";
import {
  updatePaymentRequest,
  usePaymentRequestByToken,
} from "services/paymentRequests";
import { theme } from "theme";

import PlaidPayButton from "./PlaidPayButton";

const useStyles = makeStyles(() =>
  createStyles({
    description: {
      fontSize: "14px",
      lineHeight: 1.5,
      marginBottom: theme.spacing(2),
    },
    detailSection: {
      marginBottom: theme.spacing(1),
    },
    detailLabel: {
      fontSize: "12px",
      color: theme.palette.text.secondary,
    },
    detailValue: {
      fontSize: "14px",
      fontWeight: theme.typography.fontWeightMedium,
    },
    actionButton: {
      width: "100%",
      fontSize: "16px",
      fontWeight: 700,
      padding: "12px 24px",
      borderRadius: 8,
      textTransform: "none",
      "&:hover": {
        backgroundColor: "#45a049",
      },
    },
    completedContainer: {
      textAlign: "center",
    },
    completedTitle: {
      fontSize: "24px",
      fontWeight: 700,
      color: "#000",
      marginBottom: "16px",
    },
    completedDescription: {
      fontSize: "14px",
      color: "#000",
      marginBottom: "32px",
    },
  }),
);

interface PaymentModalProps {
  token: string;
  onClose: () => void;
}

const PaymentModal: FC<PaymentModalProps> = ({ token, onClose }) => {
  const classes = useStyles();
  const [onSubmitPayment, setOnSubmitPayment] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { data: paymentRequest, refetch } = usePaymentRequestByToken(token);

  const onSuccess = useCallback(
    async (publicToken: string) => {
      try {
        const response = await chargeInvestorWithStripe(
          publicToken,
          paymentRequest.amount,
          paymentRequest.listing_id.toString(),
        );

        if (response.success === true) {
          try {
            await updatePaymentRequest(token, {
              state: "paid",
            });
            await refetch();
            onClose();
          } catch (error) {}
        } else {
          setErrorMessage(response.message);
        }
      } catch (error) {
        setErrorMessage(DEFAULT_ERROR_MESSAGE);
      } finally {
        setOnSubmitPayment(false);
      }
    },
    [paymentRequest, refetch, onClose, token],
  );

  const onExit = useCallback(() => {
    setOnSubmitPayment(false);
  }, []);

  const handleClose = useCallback(() => {
    if (!onSubmitPayment) {
      onClose();
    }
  }, [onClose, onSubmitPayment]);

  return (
    <Modal
      open
      title="Payment Details"
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="payment-dialog-title"
      disableEnforceFocus
      disableAutoFocus
      disableRestoreFocus
    >
      <Box>
        {errorMessage && (
          <Box mb={2}>
            <ErrorMessage title="Request Failed" description={errorMessage} />
          </Box>
        )}
        <Box>
          <Typography className={classes.description}>
            Please verify your bank&apos;s ACH limits <strong>before</strong>{" "}
            starting the ACH transfer process. After submitting payment, please{" "}
            <strong>do not</strong> attempt to re-submit or you may
            unintentionally overpay.
          </Typography>
          <Box className={classes.detailSection}>
            <Typography className={classes.detailLabel}>
              Payment Amount
            </Typography>
            <Typography className={classes.detailValue}>
              ${formatMoney(paymentRequest.amount)}
            </Typography>
          </Box>
          {paymentRequest.listing_title && (
            <Box className={classes.detailSection}>
              <Typography className={classes.detailLabel}>Asset</Typography>
              <Typography className={classes.detailValue}>
                {paymentRequest.listing_title}
              </Typography>
            </Box>
          )}
          <Box className={classes.detailSection}>
            <Typography className={classes.detailLabel}>
              Payment Method
            </Typography>
            <Typography className={classes.detailValue}>
              ACH Transfer
            </Typography>
          </Box>
          <Box mt={3}>
            <PlaidPayButton
              onSuccess={onSuccess}
              onExit={onExit}
              submittingPayment={onSubmitPayment}
              setSubmittingPayment={setOnSubmitPayment}
            />
          </Box>
          <Box mt={2}>
            <Typography className={classes.detailLabel}>
              For any questions or concerns{" "}
              <Link
                href={CONTACT_US_FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                color="primary"
                style={{ textDecoration: "underline" }}
                className={classes.detailLabel}
              >
                Please Contact Us.
              </Link>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default PaymentModal;
